import MetricsAggregatorABI from "@abi/MetricsAggregator";
import NodeController04ABI from "@abi/NodeController04";
import NodeController05ABI from "@abi/NodeController05";
import ReferralControllerABI from "@abi/ReferralController";
import StratosphereAbi from "@abi/Stratosphere.json";
import { TokenABI } from "@abi/TokenABI";
import VaporControllerABI from "@abi/VaporController.json";
import config from "@config";
import { createContext, useContext } from "react";

import { simpleRpcProvider } from "@utils/providers";
import { type Abi, type Address, getContract as getViemContract } from "viem";
import type { Client } from "viem";

export const getContract = <TAbi extends Abi | readonly unknown[] = Abi>({
	abi,
	address,
	publicClient,
	walletClient,
}: {
	address: Address;
	abi: TAbi;
	publicClient?: Client;
	walletClient?: Client;
}): any => {
	return getViemContract({
		abi,
		address,
		client: { public: publicClient, wallet: walletClient },
	});
};

const tokenAbi = TokenABI;

// Controllers contracts
const vaporControllerAbi = VaporControllerABI;
const provider = simpleRpcProvider();
const metricsAggregatorAbi = MetricsAggregatorABI;
const nodeController04Abi = NodeController04ABI;
const nodeController05Abi = NodeController05ABI;
const ContractContext = createContext({
	NodeController04ABI,
	NodeController05ABI,
	StratosphereAbi,
	metricsAggregatorAbi,
	metricsAggregatorContract: getContract<typeof MetricsAggregatorABI>({
		abi: MetricsAggregatorABI,
		address: config.web3.metricsAggregatorAddress as Address,
		publicClient: provider,
	}),

	nodeController04Abi,
	nodeController04Contract: getContract<typeof NodeController04ABI>({
		abi: NodeController04ABI,
		address: config.web3.nodeController04Address as Address,
		publicClient: provider,
	}),
	nodeController05Abi,
	nodeController05Contract: getContract<typeof NodeController05ABI>({
		abi: NodeController05ABI,
		address: config.web3.nodeController05Address as Address,
		publicClient: provider,
	}),
	referralControllerContract: getContract<typeof ReferralControllerABI>({
		abi: ReferralControllerABI,
		address: config.web3.referralControllerAddress as Address,
		publicClient: provider,
	}),
	stratosphereContract: getContract<typeof StratosphereAbi>({
		abi: StratosphereAbi,
		address: config.web3.stratosphereAddress as Address,
		publicClient: provider,
	}),
	tokenAbi,
	tokenContract: getContract({
		abi: tokenAbi,
		address: config.web3.vpndAddress as Address,
		publicClient: provider,
	} as any),
	vaporControllerAbi,
	vaporControllerContract: getContract({
		abi: vaporControllerAbi,
		address: config.web3.vaporControllerAddress as Address,
		publicClient: provider,
	}),
});

export default function useContract() {
	const context = useContext(ContractContext);
	if (context === undefined) {
		throw new Error("useContract must be used within a ContractContext");
	}
	return context;
}
